// Insurance & claims — PI Platform outline v2 §1.3.
//
// On the Overview tab rather than Compliance: coverage shapes how the
// file gets worked from day one, and it is the ceiling on what the case
// can be worth. Compliance is where settlement-end work lives.
//
// The card's job is to make one distinction impossible to miss:
//
//   A VERIFIED limit is a fact — a declarations page, an affidavit,
//   something produced in discovery.
//
//   An UNVERIFIED limit is a rumour. Usually an adjuster's figure over
//   the phone, which is routinely wrong or incomplete because umbrella
//   policies get forgotten.
//
// So the headline number counts only verified coverage, and unverified
// figures are shown beside it rather than added into it. Valuing a case
// on a rumour is how it settles short.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

const COVERAGE_LABELS = {
  liability_bi: "Liability (BI)", liability_pd: "Liability (PD)",
  um: "Uninsured motorist", uim: "Underinsured motorist",
  medpay: "MedPay", pip: "PIP", umbrella: "Umbrella",
  commercial_gl: "Commercial GL", other: "Other",
};

// Ordered weakest to strongest, matching the service. The labels say
// what each one actually is, so nobody picks "verbal" thinking it
// settles the question.
const METHODS = [
  ["adjuster_verbal", "Adjuster said (verbal — does not confirm)"],
  ["adjuster_letter", "Adjuster letter"],
  ["declarations_page", "Declarations page"],
  ["affidavit", "Limits-disclosure affidavit"],
  ["discovery", "Produced in discovery"],
];

const CLAIM_STATUS_LABELS = {
  opened: "Opened", investigating: "Investigating", accepted: "Accepted",
  denied: "Denied", tendered: "Tendered", closed: "Closed",
};

const money = (v) =>
  v === null || v === undefined ? "—" : `$${Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const dateOnly = (v) => (v ? String(v).slice(0, 10) : null);

const EMPTY = {
  carrier_name: "", coverage_type: "liability_bi",
  per_person_limit: "", is_client_policy: false, policy_number: "",
};

export function InsuranceCard({ matterId, showNotification }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await apiFetch(`/api/matters/${matterId}/insurance`));
    } catch (e) {
      showNotification?.(`Insurance failed to load: ${e.message}`, "error");
      setData({ policies: [], claims: [], coverage: null });
    }
  }, [matterId, showNotification]);

  useEffect(() => { load(); }, [load]);

  const addPolicy = async () => {
    if (!draft.carrier_name.trim()) {
      showNotification?.("Which carrier?", "warning");
      return;
    }
    try {
      await apiFetch(`/api/matters/${matterId}/insurance-policies`, {
        method: "POST",
        body: JSON.stringify({
          carrier_name: draft.carrier_name.trim(),
          coverage_type: draft.coverage_type,
          policy_number: draft.policy_number.trim() || null,
          is_client_policy: draft.is_client_policy,
          per_person_limit: draft.per_person_limit === "" ? null : Number(draft.per_person_limit),
        }),
      });
      setDraft(EMPTY); setAdding(false);
      await load();
    } catch (e) { showNotification?.(`Couldn't add: ${e.message}`, "error"); }
  };

  const verify = async (policy) => {
    const method = window.prompt(
      "How were the limits established?\n\n" +
      METHODS.map(([v, label], i) => `${i + 1}. ${label}`).join("\n") +
      "\n\nEnter 1-5:");
    if (!method) return;
    const picked = METHODS[Number(method) - 1];
    if (!picked) { showNotification?.("Pick 1-5.", "warning"); return; }
    const amount = window.prompt("Per-person limit (leave blank to keep the current figure):");
    try {
      const r = await apiFetch(`/api/insurance-policies/${policy.id}/verifications`, {
        method: "POST",
        body: JSON.stringify({
          method: picked[0],
          asserted_per_person: amount ? Number(amount) : null,
        }),
      });
      showNotification?.(
        r.limits_verified
          ? `${r.carrier_name} limits confirmed.`
          : `Recorded — but a ${picked[1].toLowerCase()} does not confirm the limits.`,
        r.limits_verified ? "success" : "warning");
      await load();
    } catch (e) { showNotification?.(`Failed: ${e.message}`, "error"); }
  };

  if (data === null) {
    return React.createElement("div", { style: cardStyle },
      React.createElement("div", { style: { display: "flex", alignItems: "center", gap: "8px" } },
        React.createElement(Spinner, { size: 16 }),
        React.createElement("span", { style: { fontSize: "13px", color: C.textMuted } },
          "Loading coverage…")));
  }

  const { policies, claims, coverage } = data;
  const inp = { padding: "7px 10px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
                fontSize: "13px", background: "#fff" };

  return React.createElement("div", { style: cardStyle },
    React.createElement("div", {
      style: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" },
    },
      React.createElement("div", null,
        React.createElement("h2", { style: h2Style }, "Insurance & Claims"),
        React.createElement("div", { style: { fontSize: "13px", color: C.textMuted, marginTop: "4px" } },
          policies.length === 0
            ? "No coverage recorded — the ceiling on this case is unknown."
            : `${policies.length} polic${policies.length === 1 ? "y" : "ies"}`
              + (claims.length ? ` · ${claims.length} claim${claims.length === 1 ? "" : "s"}` : "")),
      ),
      React.createElement("button", {
        onClick: () => setAdding(a => !a),
        style: { padding: "7px 14px", borderRadius: "8px", border: `1px solid ${C.navy}`,
                 background: "transparent", color: C.navy, fontSize: "12px",
                 fontWeight: 600, cursor: "pointer", flexShrink: 0 },
      }, adding ? "Cancel" : "Add coverage"),
    ),

    // ── The number that matters ─────────────────────────────
    coverage && policies.length > 0 && React.createElement("div", {
      style: { display: "flex", gap: "22px", flexWrap: "wrap", padding: "12px 14px",
               background: C.warmWhite, borderRadius: "8px", margin: "12px 0 14px" },
    },
      _stat("Verified coverage", money(coverage.verified_total),
            "confirmed in writing — safe to value the case on"),
      coverage.unverified_total ? _stat("Unverified", money(coverage.unverified_total),
            "claimed but not confirmed — excluded above") : null,
      coverage.third_party_verified ? _stat("Third-party", money(coverage.third_party_verified),
            "the other driver's coverage") : null,
      coverage.first_party_verified ? _stat("First-party", money(coverage.first_party_verified),
            "your client's own (UM/UIM/MedPay)") : null,
    ),

    // ── Add form ────────────────────────────────────────────
    adding && React.createElement("div", {
      style: { display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center",
               padding: "12px 14px", background: C.warmWhite, borderRadius: "8px", marginBottom: "14px" },
    },
      React.createElement("select", {
        value: draft.coverage_type, style: { ...inp, minWidth: "170px" },
        onChange: e => setDraft({ ...draft, coverage_type: e.target.value }),
      }, ...Object.entries(COVERAGE_LABELS).map(([v, l]) =>
        React.createElement("option", { key: v, value: v }, l))),
      React.createElement("input", {
        type: "text", value: draft.carrier_name, placeholder: "Carrier",
        onChange: e => setDraft({ ...draft, carrier_name: e.target.value }),
        style: { ...inp, flex: "1 1 180px" },
      }),
      React.createElement("input", {
        type: "text", value: draft.policy_number, placeholder: "Policy # (optional)",
        onChange: e => setDraft({ ...draft, policy_number: e.target.value }),
        style: { ...inp, width: "150px" },
      }),
      React.createElement("input", {
        type: "number", value: draft.per_person_limit, placeholder: "Per-person limit",
        onChange: e => setDraft({ ...draft, per_person_limit: e.target.value }),
        style: { ...inp, width: "150px" },
      }),
      React.createElement("label", {
        title: "Your client's own policy (UM/UIM/MedPay/PIP) rather than the other driver's.",
        style: { display: "flex", gap: "5px", alignItems: "center", fontSize: "12px", color: C.textMuted, cursor: "pointer" },
      },
        React.createElement("input", { type: "checkbox", checked: draft.is_client_policy,
          onChange: e => setDraft({ ...draft, is_client_policy: e.target.checked }) }),
        "Client's own"),
      React.createElement("button", {
        onClick: addPolicy,
        style: { padding: "8px 16px", borderRadius: "8px", border: "none", background: C.gold,
                 color: "#fff", fontSize: "12px", fontWeight: 700, cursor: "pointer" },
      }, "Add"),
    ),

    // ── Policies ────────────────────────────────────────────
    policies.length > 0 && React.createElement("div", {
      style: { border: `1px solid ${C.warmGray}60`, borderRadius: "8px", overflow: "hidden" },
    },
      ...policies.map((p, i) => React.createElement("div", {
        key: p.id,
        style: { padding: "11px 13px", borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}40` },
      },
        React.createElement("div", {
          style: { display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" },
        },
          React.createElement("div", { style: { flex: "1 1 200px", minWidth: 0 } },
            React.createElement("div", { style: { fontSize: "13px", fontWeight: 600, color: C.navy } },
              p.carrier_name),
            React.createElement("div", { style: { fontSize: "11px", color: C.textMuted, marginTop: "2px" } },
              COVERAGE_LABELS[p.coverage_type] || p.coverage_type,
              p.is_first_party ? " · client's own" : "",
              p.policy_number ? ` · ${p.policy_number}` : "")),

          React.createElement("div", { style: { fontSize: "13px", fontWeight: 600, color: C.navy, whiteSpace: "nowrap" } },
            money(p.per_person_limit)),

          // The distinction, stated in words rather than a colour.
          React.createElement("span", {
            style: {
              fontSize: "10px", fontWeight: 700, padding: "3px 9px", borderRadius: "9999px",
              whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.03em",
              background: p.limits_verified === true ? `${C.gold}20` : `${C.warmGray}60`,
              color: p.limits_verified === true ? C.navy : C.textMuted,
            },
          }, p.limits_verified === true ? "Verified"
             : p.limits_verified === false ? "Not confirmed" : "Unchecked"),

          _quiet(p.limits_verified === true ? "Re-verify" : "Verify limits", () => verify(p)),
          (p.verifications || []).length > 0 && _quiet(
            expanded === p.id ? "Hide" : "History",
            () => setExpanded(expanded === p.id ? null : p.id)),
        ),

        expanded === p.id && React.createElement("div", {
          style: { marginTop: "9px", paddingTop: "9px", borderTop: `1px solid ${C.warmGray}40`,
                   display: "flex", flexDirection: "column", gap: "4px" },
        }, ...(p.verifications || []).map(v => React.createElement("div", {
          key: v.id, style: { display: "flex", gap: "10px", fontSize: "12px", color: C.textDark },
        },
          React.createElement("span", { style: { color: C.textLight, minWidth: "88px" } },
            dateOnly(v.occurred_at) || ""),
          React.createElement("span", { style: { minWidth: "200px" } },
            (METHODS.find(m => m[0] === v.method) || [null, v.method])[1]),
          React.createElement("span", { style: { minWidth: "90px" } },
            money(v.asserted_per_person)),
          React.createElement("span", { style: { color: v.is_confirmation ? C.navy : C.textMuted } },
            v.is_confirmation ? "confirmed" : "not proof"),
          v.source_name && React.createElement("span", { style: { color: C.textMuted } }, v.source_name),
        ))),
      )),
    ),

    // ── Warnings ────────────────────────────────────────────
    coverage && (coverage.warnings || []).length > 0 && React.createElement("ul", {
      style: { margin: "12px 0 0", paddingLeft: "18px", fontSize: "12px",
               color: C.textMuted, lineHeight: 1.6 },
    }, ...coverage.warnings.map((w, i) => React.createElement("li", { key: i }, w))),

    // ── Claims ──────────────────────────────────────────────
    claims.length > 0 && React.createElement("div", {
      style: { marginTop: "14px", paddingTop: "12px", borderTop: `1px solid ${C.warmGray}60` },
    },
      React.createElement("h3", { style: h3Style }, "Claims"),
      ...claims.map(c => React.createElement("div", {
        key: c.id,
        style: { display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap",
                 fontSize: "12px", color: C.textDark, marginTop: "7px" },
      },
        React.createElement("span", { style: { fontWeight: 600, minWidth: "120px" } },
          c.claim_number || `Claim ${c.id}`),
        React.createElement("span", { style: { color: C.textMuted, minWidth: "90px" } },
          CLAIM_STATUS_LABELS[c.status] || c.status),
        c.current_adjuster && React.createElement("span", null,
          `${c.current_adjuster.name}`
          + (c.current_adjuster.phone ? ` · ${c.current_adjuster.phone}` : "")),
        (c.adjuster_history || []).length > 1 && React.createElement("span", {
          style: { color: C.textLight },
          title: c.adjuster_history.map(a => a.name).join(" → "),
        }, `(${c.adjuster_history.length - 1} previous)`),
        c.denial_reason && React.createElement("span", { style: { color: C.navy } },
          `denied: ${c.denial_reason}`),
      )),
    ),
  );
}

const cardStyle = {
  background: "#fff", borderRadius: "12px", padding: "20px 24px",
  border: "1px solid #E8E3DC", marginBottom: "20px",
};
const h2Style = {
  fontSize: "13px", color: "#6B6660", textTransform: "uppercase",
  letterSpacing: "0.08em", fontWeight: 600, margin: 0,
};
const h3Style = {
  fontSize: "12px", color: "#6B6660", textTransform: "uppercase",
  letterSpacing: "0.06em", fontWeight: 600, margin: 0,
};

function _stat(label, value, hint) {
  if (!label) return null;
  return React.createElement("div", { key: label, title: hint },
    React.createElement("div", {
      style: { fontSize: "10px", color: "#9A948C", fontWeight: 700,
               textTransform: "uppercase", letterSpacing: "0.06em" },
    }, label),
    React.createElement("div", { style: { fontSize: "17px", fontWeight: 700, color: "#1B2A4A", marginTop: "2px" } },
      value),
    React.createElement("div", { style: { fontSize: "10px", color: "#9A948C", marginTop: "1px" } }, hint));
}

function _quiet(label, onClick) {
  if (!label) return null;
  return React.createElement("button", {
    key: label, onClick,
    style: {
      border: "1px solid #E8E3DC", background: "#fff", borderRadius: "6px",
      padding: "5px 11px", fontSize: "11px", fontWeight: 600,
      color: "#1B2A4A", cursor: "pointer", flexShrink: 0,
    },
  }, label);
}
